import { parseBib, rflNumber } from './shooting';

/** Check in, Caller, and Scores. Big screen and Age groups are links, not remembered jobs. */
export const EVENT_DAY_TABS = ['check-in', 'caller', 'scores'] as const;
export type EventDayTab = (typeof EVENT_DAY_TABS)[number];

const TAB_STORAGE_PREFIX = 'smym:event-day-tab:';

export function eventDayTabKey(slug: string): string {
  return `${TAB_STORAGE_PREFIX}${slug}`;
}

function tabStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Last job this device opened for the tournament. Missing or unreadable values land on Scores. */
export function readEventDayTab(slug: string): EventDayTab {
  try {
    const value = tabStorage()?.getItem(eventDayTabKey(slug));
    if (value === 'check-in' || value === 'caller' || value === 'scores') return value;
  } catch {
    // Private mode: open Scores.
  }
  return 'scores';
}

export function writeEventDayTab(slug: string, tab: EventDayTab): void {
  try {
    tabStorage()?.setItem(eventDayTabKey(slug), tab);
  } catch {
    // The tab still changes for this visit.
  }
}

export interface ShooterQueryRef {
  name: string;
  bib: string | null;
}

/**
 * A scorer's search. A bare number matches that bib and nothing nearby:
 * "23" finds RFL-0023, not RFL-0123 or RFL-2300. "0023", "RFL-0023", and "rfl 23" match the same row.
 * Any other text matches the name, and also the bib when it is not itself a bib number.
 */
export function shooterMatchesQuery(shooter: ShooterQueryRef, query: string, prefix = 'RFL'): boolean {
  const raw = query.trim();
  const needle = raw.toLowerCase();
  if (!needle) return false;
  const nameHit = shooter.name.toLowerCase().includes(needle);
  if (/^\d{1,6}$/.test(raw)) {
    const wanted = Number(raw);
    return nameHit || rflNumber(shooter.bib, prefix) === wanted;
  }
  const parsed = parseBib(raw, prefix);
  if (parsed.ok && parsed.bib) {
    const wanted = rflNumber(parsed.bib, prefix);
    return nameHit || (wanted != null && rflNumber(shooter.bib, prefix) === wanted);
  }
  return nameHit || (shooter.bib ?? '').toLowerCase().includes(needle);
}

/** Search hits in roster order. An empty box matches nobody, so the lane list stays the default. */
export function filterShootersByQuery<T extends ShooterQueryRef>(
  shooters: readonly T[],
  query: string,
  prefix = 'RFL',
): T[] {
  if (!query.trim()) return [];
  return shooters.filter((shooter) => shooterMatchesQuery(shooter, query, prefix));
}

export interface LaneAdvanceSlot {
  lane: number;
  shooterId: string | null;
  /** True once this shooter has a saved score. */
  saved: boolean;
}

/**
 * The next unsaved shooter on the lanes, lane 1 through lane 6.
 * Empty lanes are skipped. After the last unsaved lane it wraps to an earlier one.
 * Null when nobody is on a lane, or every occupied lane is already saved.
 */
export function nextUnsavedOnLanes(lanes: readonly LaneAdvanceSlot[], currentShooterId: string | null): string | null {
  const occupied = [...lanes]
    .filter((lane): lane is LaneAdvanceSlot & { shooterId: string } => Boolean(lane.shooterId))
    .sort((a, b) => a.lane - b.lane);
  const unsaved = occupied.filter((lane) => !lane.saved);
  if (unsaved.length === 0) return null;
  if (!currentShooterId) return unsaved[0]!.shooterId;
  const currentLane = occupied.find((lane) => lane.shooterId === currentShooterId)?.lane;
  if (currentLane == null) return unsaved[0]!.shooterId;
  return (unsaved.find((lane) => lane.lane > currentLane) ?? unsaved[0]!).shooterId;
}

/** True when at least one lane is occupied and each of those shooters has a saved score. */
export function allLanesScored(lanes: readonly LaneAdvanceSlot[]): boolean {
  const occupied = lanes.filter((lane) => lane.shooterId);
  return occupied.length > 0 && occupied.every((lane) => lane.saved);
}

/** `Saved: Rita Mehta 98.4` — the score is the best-N total the board uses. */
export function savedScoreToast(name: string, score: string): string {
  return `Saved: ${name.trim()} ${score.trim()}`;
}
